/*
 * Human Standard — measurement layer (transport + PII guard)
 * ===========================================================================
 * IMPORTANT — read before extending.
 *
 * This file does NOT install GA4, Google Tag Manager or a Shopify Web Pixel.
 * No measurement ID exists in this repository, and one must not be invented.
 * This layer only *transports* events to whichever collectors are already
 * present on the page, and refuses to transport personal data.
 *
 * Collectors, if present:
 *   - window.dataLayer            (GTM / GA4 via GTM)
 *   - window.gtag                 (GA4 via Google tag)
 *   - Shopify.analytics.publish   (Shopify Customer Events)
 *
 * Page views are intentionally NOT sent here. GA4 and Shopify both emit their
 * own page_view. Adding another would double-count every session.
 *
 * PRIVACY BOUNDARY — never send, and this file actively strips:
 *   email, name, phone, street address, ZIP/postal code, free-form text,
 *   complete quiz answer payloads, health information, Supabase identifiers,
 *   Klaviyo profile identifiers, auth tokens.
 * Quiz scoring, Supabase submission and Klaviyo submission live in
 * sections/home-wellness-quiz-free.liquid and must not be routed through here.
 * ===========================================================================
 */
(function () {
  'use strict';

  /*
   * Parameter keys that must never reach a collector, regardless of value.
   * Matching is substring-based and case-insensitive so that variants such as
   * `customer_email` or `zipCode` are caught too.
   */
  var BLOCKED_KEY_PATTERNS = [
    'email',
    'e_mail',
    'name', // first_name, last_name, full_name; booleans use has_first_name
    'phone',
    'tel',
    'address',
    'street',
    'zip',
    'postal',
    'city',
    'lat',
    'lng',
    'dob',
    'birth',
    'age',
    'gender',
    'household',
    'health',
    'condition',
    'allerg',
    'asthma',
    'pregnan',
    'diagnos',
    'answers',
    /*
     * `answer_value` / `answer_text` can carry a household's own words about
     * children, pregnancy or health. The quiz section has its own narrower
     * sanitiser for the events it already sends; this layer refuses the whole
     * family of keys because nothing routed through here needs them.
     */
    'answer_value',
    'answer_text',
    'response_text',
    'free_text',
    'comment',
    'note',
    'message',
    'profile',
    'traits',
    'token',
    'auth',
    'secret',
    'api_key',
    'apikey',
    'supabase',
    'klaviyo',
    'customer_id',
    'user_id',
    'ip'
  ];

  /* Keys that read like blocked ones but are safe aggregate booleans/counts. */
  var ALLOWED_EXCEPTIONS = ['has_first_name', 'has_kids', 'event_name'];

  var EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
  var ZIP_RE = /^\d{5}(-\d{4})?$/;
  var PHONE_RE = /(\+?\d[\d\s().-]{7,})/;
  var MAX_STRING_LENGTH = 80; // longer values are treated as free-form text

  function isBlockedKey(key) {
    var lower = String(key).toLowerCase();
    if (ALLOWED_EXCEPTIONS.indexOf(lower) !== -1) return false;
    for (var i = 0; i < BLOCKED_KEY_PATTERNS.length; i++) {
      if (lower.indexOf(BLOCKED_KEY_PATTERNS[i]) !== -1) return true;
    }
    return false;
  }

  function isBlockedValue(value) {
    if (typeof value !== 'string') return false;
    if (value.length > MAX_STRING_LENGTH) return true;
    if (EMAIL_RE.test(value)) return true;
    if (ZIP_RE.test(value.trim())) return true;
    if (PHONE_RE.test(value)) return true;
    return false;
  }

  /*
   * Returns a shallow, primitive-only copy of `params`. Objects are dropped
   * rather than flattened, because nested objects are how full quiz/profile
   * payloads have historically leaked into analytics.
   */
  function sanitize(params) {
    var clean = {};
    if (!params || typeof params !== 'object') return clean;

    Object.keys(params).forEach(function (key) {
      if (isBlockedKey(key)) return;

      var value = params[key];
      if (value === null || value === undefined || value === '') return;

      if (Array.isArray(value)) {
        var items = value.filter(function (item) {
          return (
            (typeof item === 'string' || typeof item === 'number') && !isBlockedValue(item)
          );
        });
        if (items.length) clean[key] = items.slice(0, 10).join(',');
        return;
      }

      if (typeof value === 'object') return; // never forward nested payloads
      if (isBlockedValue(value)) return;

      clean[key] = value;
    });

    return clean;
  }

  /* Path only — query strings and fragments can carry user-supplied values. */
  function safePath() {
    try {
      return window.location.pathname || '/';
    } catch (error) {
      return '/';
    }
  }

  /*
   * Campaign attribution is the one query-string read we allow, limited to the
   * five standard UTM keys.
   */
  function utmParams() {
    var out = {};
    try {
      var search = new URLSearchParams(window.location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(
        function (key) {
          var value = search.get(key);
          if (value && !isBlockedValue(value)) out[key] = value.slice(0, MAX_STRING_LENGTH);
        }
      );
    } catch (error) {
      /* no-op */
    }
    return out;
  }

  /*
   * Preview, theme-editor and development traffic should be distinguishable so
   * it can be filtered in GA4. This is a hint only — the authoritative
   * exclusion must be configured in GA4 Admin (see the refinement report).
   */
  function trafficType() {
    try {
      if (window.Shopify && window.Shopify.designMode) return 'theme_editor';
      if (window.location.search.indexOf('preview_theme_id') !== -1) return 'theme_preview';
      var host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') return 'development';
      if (host.indexOf('.myshopify.com') !== -1) return 'internal_preview';
    } catch (error) {
      /* no-op */
    }
    return 'production';
  }

  function track(eventName, params) {
    if (!eventName || typeof eventName !== 'string') return;

    var clean = sanitize(params);
    var envelope = Object.assign(
      { event: eventName, page_path: safePath(), traffic_type: trafficType() },
      utmParams(),
      clean
    );

    try {
      var consent = window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.currentVisitorConsent === 'function'
        ? window.Shopify.customerPrivacy.currentVisitorConsent()
        : null;
      if (!consent || consent.analytics !== 'yes') return;
    } catch (error) {
      return;
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(envelope);
    } catch (error) {
      /* no-op */
    }

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, Object.assign({ traffic_type: trafficType() }, clean));
      }
    } catch (error) {
      /* no-op */
    }

    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, envelope);
      }
    } catch (error) {
      /* no-op */
    }
  }

  window.KnownAnalytics = { track: track, sanitize: sanitize, trafficType: trafficType };

  /* -------------------------------------------------------------------------
   * Declarative bindings
   *
   * Mark up an element with:
   *   data-kn-track="event_name"
   *   data-kn-track-<param>="value"      (kebab-case becomes snake_case)
   *
   * Values are still sanitized, so a template cannot accidentally leak PII.
   * ---------------------------------------------------------------------- */
  function paramsFromDataset(el) {
    var params = {};
    Object.keys(el.dataset).forEach(function (key) {
      if (key.indexOf('knTrack') !== 0 || key === 'knTrack') return;
      var name = key
        .slice('knTrack'.length)
        .replace(/^[A-Z]/, function (c) {
          return c.toLowerCase();
        })
        .replace(/[A-Z]/g, function (c) {
          return '_' + c.toLowerCase();
        });
      params[name] = el.dataset[key];
    });
    return params;
  }

  document.addEventListener(
    'click',
    function (event) {
      var el = event.target.closest && event.target.closest('[data-kn-track]');
      if (!el) return;
      track(el.dataset.knTrack, paramsFromDataset(el));
    },
    true
  );

  /* "Why we ask" / expandable educational content. */
  document.addEventListener(
    'toggle',
    function (event) {
      var el = event.target;
      if (!el || el.tagName !== 'DETAILS' || !el.hasAttribute('data-kn-track-expand')) return;
      if (!el.open) return;
      track('educational_disclosure_expanded', {
        disclosure_key: el.getAttribute('data-kn-track-expand'),
        page_type: el.getAttribute('data-kn-page-type') || undefined
      });
    },
    true
  );

  /*
   * Form funnel: view (enters viewport) -> start (first field interaction) ->
   * submit. Field *values* are never read.
   */
  function bindForms() {
    var forms = document.querySelectorAll('[data-kn-form]');
    if (!forms.length) return;

    Array.prototype.forEach.call(forms, function (form) {
      if (form.dataset.knFormBound === 'true') return;
      form.dataset.knFormBound = 'true';

      var formName = form.getAttribute('data-kn-form');

      if ('IntersectionObserver' in window) {
        var viewObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              track('form_viewed', { form_name: formName });
              viewObserver.unobserve(entry.target);
            });
          },
          { threshold: 0.4 }
        );
        viewObserver.observe(form);
      }

      var started = false;
      form.addEventListener(
        'focusin',
        function () {
          if (started) return;
          started = true;
          track('form_started', { form_name: formName });
        },
        true
      );

      form.addEventListener('submit', function () {
        track('form_submitted', { form_name: formName });
      });
    });
  }

  bindForms();
  document.addEventListener('shopify:section:load', bindForms);
})();
